import { useMemo, useState } from 'react';
import { useI18n, useT } from '../lib/i18n';
import { formatters, type Day } from '../lib/dates';
import type { Party } from '../lib/party';
import { LengthPicker } from '../components/LengthPicker';
import { DateRange } from '../components/DateRange';
import { PartyPicker } from '../components/PartyPicker';
import { Filters } from '../components/Filters';
import { useHost } from './WidgetRoot';
import { useSearch } from './useSearch';
import { tileResults } from './TileResults';
import type { ResultsRenderer } from './results';

/**
 * The search widget: controls, and a renderer it does not choose.
 *
 * ---------------------------------------------------------------------------
 * ONE COMPONENT, TWO SHAPES, TWO HOSTS
 * ---------------------------------------------------------------------------
 *
 * `scopedTo` narrows everything to one accommodation — the variant that sits on
 * that accommodation's own WordPress post, showing its price, its availability
 * and how it may be booked. It is the same component because it is the same
 * behaviour over a set of size one; a second "just show a price" component is
 * where the two would drift, and the one living in a plugin is the one that
 * would rot.
 *
 * `renderResults` decides what the answers look like. This app passes the tile
 * renderer; WordPress passes `domResults`, which draws nothing and instead
 * shows, hides and price-stamps the cards the park's theme already rendered.
 * Everything above that line — the controls, the availability recomputation,
 * the prices — is identical in both, which is the whole reason the seam is
 * where it is. See `results.ts`.
 *
 * The search LOGIC is not in this file at all. It is `useSearch`, so a host
 * that wants only the logic can have it without also taking these controls.
 */
export function SearchWidget({
  scopedTo, kinds, initial, renderResults = tileResults, showControls = true,
  showFilters = true,
  showResults = true, monthNav,
}: {
  /** Restricts everything to one accommodation — the single-post variant. */
  scopedTo?: string | readonly string[];
  /** Narrows to a kind of accommodation. See `WidgetOptions.kinds`. */
  kinds?: readonly string[];
  initial?: { arrival?: Day; nights?: number; party?: Party };
  renderResults?: ResultsRenderer;
  /**
   * Whether to draw the controls.
   *
   * A park whose WordPress page already has its own date fields can turn these
   * off and drive the widget from `initial`, which is the one case where a host
   * legitimately owns the inputs as well as the output.
   */
  showControls?: boolean;
  /**
   * Whether to draw the attribute filters.
   *
   * Off for the compat bridge, which frames a fixed strip on somebody else's
   * page. A rendering decision only: availability is still computed across a
   * SET of types, and turning this off makes the set "all of them".
   */
  showFilters?: boolean;
  /**
   * Draw the results section, or only run the renderer.
   *
   * False where the ANSWERS belong to the host page: the compat bridge's tile
   * search posts each price onto a tile the CMS already drew, so the renderer
   * returns nothing to place. The heading and the spinner were drawn anyway,
   * which put "Wat we kunnen aanbieden" inside the frame with nothing under it
   * — the prices being on the page below, outside the iframe entirely.
   *
   * The renderer still runs. It has to: posting the results IS its effect, and
   * a widget that stopped rendering it would stop answering the host.
   */
  showResults?: boolean;
  /**
   * How the guest moves between months. Undefined follows the convention every
   * major booking site shares — paging on a desktop, scrolling on a phone.
   * The compat bridge asks for `'paged'`; see `DateRange` for why a frame
   * cannot have the mobile half of that convention.
   */
  monthNav?: 'paged' | 'scroll';
}) {
  const t = useT();
  const { locale } = useI18n();
  const host = useHost();
  const fmt = useMemo(
    () => formatters(locale, host.config.currency),
    [locale, host.config.currency],
  );

  const search = useSearch({ scopedTo, kinds, initial });

  // Read once, not subscribed to. A guest who rotates their phone mid-search
  // has already decided whether the panel should be open, and reopening it
  // under them would undo that.
  const [filtersOpen, setFiltersOpen] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 900px)').matches
      : true
  ));

  if (search.loadFailed) {
    return (
      <div className="pw-panel pw-stack" role="alert">
        <p>{t('error.load')}</p>
        <button type="button" className="pw-btn" onClick={search.retry}>
          {t('error.retry')}
        </button>
      </div>
    );
  }

  const view = {
    results: search.results,
    all: search.all,
    attributes: search.allDefs,
    arrival: search.arrival,
    nights: search.nights,
    quoting: search.quoting,
    chosen: search.chosen,
    choose: search.choose,
    currency: host.config.currency,
  };

  const stepHeading = (
    n: number,
    key: 'search.stepLength' | 'search.stepDates' | 'search.stepParty',
    id: string,
  ) => (
    <div className="pw-step-head">
      <h3 className="pw-h3" id={id}>{t(key)}</h3>
      <span className="pw-eyebrow">{t('search.step', { n })}</span>
    </div>
  );

  return (
    <div className="pw-stack">
      {showControls && (
        <section className="pw-panel pw-stack" aria-labelledby="pw-search-heading">
          <h2 className="pw-h2" id="pw-search-heading">
            {scopedTo ? t('unit.heading') : t('search.heading')}
          </h2>

          {/* Step one is the dates now, and there is no step that gates it.
              The length used to come first because a month of availability cost
              42 requests and could only be afforded once a length was known;
              `/api/public/calendar` sends the window in one, so the guest is
              asked the question they came with. See DateRange. */}
          <div className="pw-step">
            {stepHeading(1, 'search.stepDates', 'pw-step-dates')}
            <DateRange
              arrival={search.arrival}
              departure={search.departure}
              onChange={search.setDates}
              arrivable={search.arrivable}
              departures={search.departures}
              minDay={search.minDay}
              monthsFrom={search.monthsFrom}
              monthCount={search.monthCount}
              onNeedMonths={search.needMonths}
              monthNav={monthNav}
              unknownBeyond={search.unknownBeyond}
              emptyFilter={search.emptyFilter}
              loading={search.scanning}
            />

            {/* An accelerator, and only once there is an arrival to measure
                from. A guest who thinks in weeks keeps their shortcut; one who
                thinks in dates never sees a question they cannot answer. */}
            {search.arrival !== null && (
              <LengthPicker
                nights={search.nights}
                onChange={search.setNights}
                headingId="pw-step-dates"
              />
            )}

            {search.emptyFilter && (
              <p className="pw-banner" role="status">{t('search.filterEmpty')}</p>
            )}
            {search.scanFailed && (
              <p className="pw-banner pw-banner-crit" role="alert">{t('error.availability')}</p>
            )}
            {/* Only where there are filters to have been applied. With the
                controls hidden it reads as a report about a narrowing the guest
                never did — and cannot see, or undo. */}
            {!scopedTo && showFilters && search.filterDefs.length > 0 && search.arrivable && (
              <p className="pw-note">
                {t('search.filteredFor', {
                  count: t('search.matches', { count: search.results.length }),
                })}
              </p>
            )}
          </div>

          <div className="pw-step">
            {stepHeading(2, 'search.stepParty', 'pw-step-party')}
            <PartyPicker
              /* What this widget is about, not the whole park: a rental page
                 must not offer the camping dog, and an age band must come from
                 a card the guest can actually choose. */
              accommodations={search.inScope}
              party={search.party}
              onChange={search.setParty}
              labelledBy="pw-step-party"
            />
          </div>
        </section>
      )}

      <div className={showFilters && search.filterDefs.length > 0 ? 'pw-split' : undefined}>
        {showFilters && search.filterDefs.length > 0 && (
          /*
           * A disclosure, open on a wide screen and closed on a phone.
           *
           * The filter column sits above the results below 900px, so leaving it
           * expanded puts a screenful of controls between a guest and the thing
           * they came for. `<details>` rather than a state-driven `hidden`,
           * because the browser already gets the keyboard and the screen-reader
           * announcement right and the contents stay in the document either way.
           */
          <details
            className="pw-aside pw-panel pw-disclosure"
            open={filtersOpen}
            onToggle={(e) => setFiltersOpen(e.currentTarget.open)}
          >
            <summary>{t('filters.heading')}</summary>
            <Filters
              defs={search.filterDefs}
              accommodations={search.catalogue?.accommodations ?? []}
              state={search.filters}
              onChange={search.setFilters}
            />
          </details>
        )}

        {showResults ? (
          <section className="pw-stack" aria-labelledby="pw-results-heading">
            <div className="pw-row">
              <h2 className="pw-h2" id="pw-results-heading">
                {scopedTo ? t('unit.heading') : t('results.heading')}
              </h2>
              {search.quoting && (
                <span className="pw-busy">
                  <span className="pw-spinner" aria-hidden="true" />
                  {t('results.quoting')}
                </span>
              )}
            </div>
            {renderResults(view)}
          </section>
        ) : (
          // No section, no heading, no spinner — but the renderer still runs,
          // because posting the prices to the host page is what it does.
          renderResults(view)
        )}
      </div>
    </div>
  );
}
